import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="40812" className="hidden md:flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-16 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn(navigationMenuTriggerStyle(), "bg-transparent hover:bg-transparent focus:bg-transparent data-[active]:bg-transparent data-[state=open]:bg-transparent", "text-foreground/90 hover:text-foreground font-sans font-medium tracking-tight transition-colors relative", "after:absolute after:bottom-1 after:left-4 after:right-4 after:h-[1.5px] after:bg-primary after:scale-x-0 after:origin-center after:transition-transform hover:after:scale-x-100")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return null;
  return <nav className="flex flex-col gap-6 items-center justify-center">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-2xl font-sans font-medium text-foreground hover:text-primary transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
      <Button asChild variant="outline" size="lg" className="mt-4 w-full max-w-xs border-primary text-primary hover:bg-primary hover:text-primary-foreground">
        <Link to="#" onClick={onClose}>
          Get Started
        </Link>
      </Button>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 ease-in-out bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border/10 py-3" : "bg-background py-5 border-b border-transparent")}>
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569965/img/logo_the_animation_agency.png" alt="KaleighWrites" className="h-7 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="45">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex flex-shrink-0">
          <Button asChild variant="outline" size="sm" className="font-sans font-medium border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            <Link to="#">Get Started</Link>
          </Button>
        </div>

        {}
        <div className="md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" className="text-foreground">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 top-[60px] z-40 bg-background/95 backdrop-blur-sm md:hidden animate-in fade-in slide-in-from-top-5 duration-200">
          <div className="container mx-auto px-4 py-8 h-full flex flex-col">
            <MenuProvider menu_id="45">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>}
    </header>;
}